import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { Alert } from "@mui/material";
import SettingsTab from "../../Device/Tabs/SettingsTab";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";

/**
 * Cihaz Ayarlar drawer — QuotaOperationDrawer / ManualQuotaDrawer ile aynı tasarım.
 * Genel Ayarlar ve Göz Ayarları içeriği SettingsTab ile aynı.
 */
const DeviceSettingsDrawer = ({ open, onClose, device: deviceProp }) => {
  // Giriş/çıkış kayması + kapanış boyunca cihazın dondurulması (çağıran taraf
  // kapatırken device'ı null'a çekiyor, donmazsa içerik ekrandayken boşalır).
  const { mounted, entered, data: device } = useDrawerTransition(open, deviceProp);
  const [alerts, setAlerts] = useState([]);

  // Sıfırlama kapanış animasyonu bitince (mounted false) yapılır.
  useEffect(() => {
    if (!mounted) {
      setAlerts([]);
    }
  }, [mounted]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";
    return () => { document.body.style.overflow = "auto"; };
  }, [open]);

  const setAlertsCallback = (updater) => {
    setAlerts((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return next;
    });
  };

  if (!mounted || !device) return null;

  const deviceName = device.customName || device.deviceName || "";
  const serialNo = device.serialNo ?? "";

  return (
    <>
      <div
        className={`fixed inset-y-0 right-0 h-full w-full sm:w-[520px] md:w-[600px] lg:w-[700px] xl:w-[800px] 2xl:w-[880px] max-w-[100vw] transform ${
          entered ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out shadow-2xl z-[9999] flex flex-col bg-white overflow-hidden`}
      >
        <div className="bg-gradient-to-r from-slate-600 to-blue-700 px-5 py-5 text-white shrink-0">
          <div className="flex items-center justify-between min-h-[2.5rem]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold tracking-wide leading-tight">Ayarlar</h3>
                <p className="text-xs text-white/80 mt-1 truncate">
                  {deviceName} • {serialNo}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/10 hover:bg-white/20"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>

        {alerts.length > 0 && (
          <div className="px-4 pt-3 space-y-2 shrink-0">
            {alerts.map((alert) => (
              <Alert
                key={alert.id}
                severity={alert.severity}
                variant="filled"
                onClose={() => setAlerts((prev) => prev.filter((a) => a.id !== alert.id))}
              >
                {typeof alert.message === "string" ? alert.message : "Bir hata oluştu"}
              </Alert>
            ))}
          </div>
        )}

        {/* Yatay padding YOK: SettingsTab kendi sabit başlığını drawer'ın
            kenarına kadar uzatıyor (alt kenarlık tam satır) ve içerik
            boşluğunu kendi içinde veriyor. pb yalnızca home-indicator/safe
            area içindir. */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden pb-[env(safe-area-inset-bottom,0px)]">
          <SettingsTab selectedDevice={device} setAlerts={setAlertsCallback} isDrawer />
        </div>
      </div>

      {mounted && (
        <div
          className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
          onClick={onClose}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default DeviceSettingsDrawer;
